import omit from 'lodash/omit';
import pick from 'lodash/pick';

import {ProductUpdate} from 'app_api/product_updates/types';
import {Section} from 'common/components/items-menu/types';
import {ReportName} from 'common/report/constants';
import type {TourId} from 'common/util/chameleon';

import {ALL_PRODUCT_UPDATES_LINK, HelpSectionItemType, NavCategoryType, REPORT_INFO} from './constants';
import {HelpProductUpdateSectionItemValue, NavCategories, NavReports, ReportContext} from './types';

function getNavReports(reportContext: ReportContext): NavReports {
  return Object.keys(REPORT_INFO).reduce((obj, key) => {
    const report = REPORT_INFO[key];
    const result = {
      ...omit(report, `disabling`),
      name: key,
      endpoint: report.endpoint && report.endpoint(reportContext),
      hidden: report.hidden && report.hidden(reportContext),
      disabled: report.disabling?.isDisabled(reportContext) && report.disabling.disabledMsg,
    };
    obj[key] = result;
    return obj;
  }, {});
}

export function getNavReportsFromCategory(categoryType: NavCategoryType, reportContext: ReportContext): NavReports {
  const navReports = getNavReports(reportContext);
  return Object.keys(navReports)
    .filter((report) => navReports[report].category === categoryType && !navReports[report].hidden)
    .reduce((obj, key) => {
      obj[key] = navReports[key];
      return obj;
    }, {});
}

export function getNavCategories({reportContext}: {reportContext: ReportContext}): NavCategories {
  const getReports = function (categoryType) {
    return getNavReportsFromCategory(categoryType, reportContext);
  };

  const categories: NavCategories = {
    analysis: {
      label: NavCategoryType.Analysis,
      twoColumn: true,
      reports: getReports(NavCategoryType.Analysis),
    },

    users: {
      label: NavCategoryType.Users,
      reports: pick(getReports(NavCategoryType.Users), ReportName.Users),
    },

    dataManagement: {
      label: NavCategoryType.DataManagement,
      reports: getReports(NavCategoryType.DataManagement),
    },
  };

  // set name for each category and report based on its prop key and filter out hidden objects
  for (const [categoryName, category] of Object.entries(categories)) {
    category.name = categoryName;
    category.hidden && delete categories[categoryName];

    for (const [reportName, report] of Object.entries(category.reports)) {
      if (report.hidden) {
        delete category.reports[reportName];
      }
    }
  }

  return categories;
}

export function getProductUpdateSections(updates: ProductUpdate[]): Array<Section<HelpProductUpdateSectionItemValue>> {
  if (!updates.length) {
    return null;
  }

  return [
    {
      items: [
        ...updates.map((update) => ({
          label: update.label,
          value: {
            helpLink: update.link,
            tourId: update.chameleon_tour_id as TourId,
            reportName: update.report_name,
            type: HelpSectionItemType.ProductUpdate,
          } as HelpProductUpdateSectionItemValue,
        })),
        {
          label: `All Product Updates`,
          value: {
            helpLink: ALL_PRODUCT_UPDATES_LINK,
            type: HelpSectionItemType.ProductUpdate,
          },
        },
      ],
    },
  ];
}
